/**
 * The Picklii mark: the Brand Kit v3 "p" icon (the wordmark's own "p", with
 * two sage dots above its stem echoing the wordmark's "ii") beside the
 * wordmark — product direction confirmed 2026-10-25 that this lockup is
 * wanted everywhere (navbar, footer, demo video), overriding the kit
 * README's own instruction against pairing the icon with the wordmark.
 *
 * The icon's "p" uses `currentColor` rather than the kit's own fixed navy:
 * navy is close to invisible against this site's dark-theme surfaces (navy
 * and the dark theme's `base-200`/`base-100` sit at almost identical
 * lightness), same reasoning as the Brand Kit v2 fix this one succeeds.
 * `currentColor` inherits the already-correct ambient text colour instead.
 * The two dots stay pinned to the brand sage, same as the wordmark's `ii`.
 *
 * `pickl` is deliberately left to inherit the surrounding text colour rather
 * than being pinned to the brand navy: `base-content` in the light theme
 * *is* that navy (see `styles.css`), so it already renders correctly there,
 * and in the dark theme `base-content` is the kit's own reversed-wordmark
 * cream — so this one rule reproduces both the kit's regular and reversed
 * wordmark colourings with no image swap needed. Only the trailing `ii` is
 * pinned, to `text-primary` (the brand sage), the accent the kit's wordmark
 * uses in both themes.
 *
 * The brand kit's production wordmark uses Poppins. It is not loaded here:
 * `apps/web` ships no web font today, and SPEC-0005 commits the marketing
 * site to zero third-party fonts or assets — loading one for the app alone
 * would put the two surfaces visibly out of sync, which is the thing
 * `theme-parity.test.ts` exists to prevent for colour. `tech-stack.md` §6
 * records this as a deliberate gap, not an oversight.
 */
export function Logo({
	className = 'gap-1.5 text-lg',
	iconClassName = 'size-6',
}: {
	className?: string;
	iconClassName?: string;
}) {
	return (
		<span className={`inline-flex items-center font-semibold tracking-tight ${className}`}>
			<svg viewBox="0 0 1080 1080" className={iconClassName} aria-hidden="true">
				<path
					fill="currentColor"
					d="M615.2 206.5Q684.2 206.5 740.2 241.5Q796.2 276.5 828.8 341.5Q861.2 406.5 861.2 492.5Q861.2 578.5 828.8 644Q796.2 709.5 740.2 745Q684.2 780.5 615.2 780.5Q557.2 780.5 512.8 756.5Q468.2 732.5 443.2 694.5V1038.5H272.2V214.5H443.2V293.5Q468.2 254.5 512.2 230.5Q556.2 206.5 615.2 206.5ZM564.2 355.5Q513.2 355.5 477.8 392.5Q442.2 429.5 442.2 493.5Q442.2 557.5 477.8 594.5Q513.2 631.5 564.2 631.5Q615.2 631.5 651.2 594Q687.2 556.5 687.2 492.5Q687.2 428.5 651.8 392Q616.2 355.5 564.2 355.5Z"
				/>
				<ellipse cx="283.8" cy="100.5" rx="65.0" ry="59.0" fill="#5B8C6E" />
				<ellipse cx="431.8" cy="100.5" rx="65.0" ry="59.0" fill="#5B8C6E" />
			</svg>
			<span aria-hidden="true">
				pickl<span className="text-primary">ii</span>
			</span>
			<span className="sr-only">Picklii</span>
		</span>
	);
}
